//@flow
import React, {useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import CustomTextField from '../../../../../../../../elements/CustomTextField/CustomTextField';
import {TRIGGER_FIELDS, TEXT_PATTERN_OPTIONS} from '../../../const';
import {
  FormControlLabel,
  Checkbox,
  MenuItem,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomSelect from '../../../../../../../../elements/CustomSelect/CustomSelect';
import SimilarWordsInput from './SimilarWordsInput';
import {sanitizeSimilarWords} from '../../../../../utils/adapters';
import {
  FILTER_SET_VALUE_OPTIONS,
  setFilterFieldUpdates,
} from '../../../../../utils/filterFields';
import {FILTER_TYPES} from '../../../../../utils/triggerConfig';
import {getFormError} from '../../../../../utils/formErrors';
import {colors} from '../../../../../../../../assets/styles/colors';
import type {TriggerTextSearchFilter as TriggerTextSearchFilterValues} from '../../../../../../../../entities';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  patternSelect: {
    maxWidth: '200px',
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
    marginTop: '.25rem',
  },
}));

type Props = {
  filterType: typeof FILTER_TYPES.TRIGGER_TEXT_SEARCH,
};

export default function TextSearchFilter({filterType}: Props) {
  const classes = useStyles();
  const {register, watch, errors, setValue, getValues} = useFormContext();

  const fieldPrefix = `${TRIGGER_FIELDS.filters}.${filterType}`;

  useEffect(() => {
    // Register nested paths for setError display only — values are owned by
    // the parent `filters` object (see setFilterFieldUpdates).
    register(`${fieldPrefix}.searchText`);
    register(`${fieldPrefix}.searchTextPattern`);
    register(`${fieldPrefix}.enabledRelatedTexts`);
    register(`${fieldPrefix}.relatedSearchTexts`);
  }, [register, fieldPrefix]);

  const allFilters = watch(TRIGGER_FIELDS.filters) || {};
  const filter: TriggerTextSearchFilterValues = allFilters[filterType] || {};
  const considerSimilarWord = !!filter.enabledRelatedTexts;
  const similarWords = sanitizeSimilarWords(filter.relatedSearchTexts || []);
  const searchTextError = getFormError(errors, `${fieldPrefix}.searchText`);
  const relatedTextsError = getFormError(
    errors,
    `${fieldPrefix}.relatedSearchTexts`
  );

  const patchFilter = (fieldUpdates: {[key: string]: any}) => {
    setFilterFieldUpdates(
      getValues,
      setValue,
      filterType,
      fieldUpdates,
      FILTER_SET_VALUE_OPTIONS
    );
  };

  return (
    <div className={classes.root}>
      <CustomSelect
        className={classes.patternSelect}
        placeholder="Seleccionar patrón"
        onChange={event => {
          patchFilter({searchTextPattern: event.target.value});
        }}
        value={filter.searchTextPattern || 'MATCHES_WITH'}
      >
        {TEXT_PATTERN_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>

      <CustomTextField
        name={`${fieldPrefix}.searchText`}
        placeholder="Escribe el contenido/palabra"
        value={filter.searchText || ''}
        onChange={event => {
          patchFilter({searchText: event.target.value});
        }}
        onBlur={event => {
          patchFilter({searchText: event.target.value.trim()});
        }}
        error={!!searchTextError}
        helperText={searchTextError}
      />

      <FormControlLabel
        control={
          <Checkbox
            onChange={e => {
              const isEnabled = e.target.checked;
              if (!isEnabled) {
                patchFilter({
                  enabledRelatedTexts: false,
                  relatedSearchTexts: [],
                });
                return;
              }

              patchFilter({enabledRelatedTexts: true});
            }}
            color="primary"
            checked={considerSimilarWord}
          />
        }
        label="Considerar palabras similares"
      />

      {considerSimilarWord && (
        <>
          <SimilarWordsInput
            words={similarWords}
            onChange={nextWords => {
              patchFilter({
                relatedSearchTexts: sanitizeSimilarWords(nextWords),
              });
            }}
          />
          {relatedTextsError && (
            <Typography className={classes.fieldError}>
              {relatedTextsError}
            </Typography>
          )}
        </>
      )}
    </div>
  );
}
